import { DATA_LOCATION_PATH } from "@/hooks/use-data-location";
import { instanceKey } from "@/lib/instanceKey";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  BrowseDataflowBody,
  BrowseView,
  CreateDataflowBody,
  DataflowDetail,
  DataflowEntityView,
  DataflowSummary,
  PatchEntitiesBody,
  UpdateDataflowBody,
  ValidateDataflowBody,
  ValidationView,
} from "@shared/types/dataflows";
import type { EntitiesQuery } from "@shared/types/entities";
import { CREDENTIALS_PATH, apiRequest, apiRequestEnvelope } from "@/lib/api";
import { listQueryOptions } from "@/lib/listQuery";

/**
 * Connections (FR-41 to FR-46) as the screens consume them.
 *
 * One module owns every dataflow call so the cache keys, the invalidation and
 * the request bodies are stated once. Every body below is exactly what
 * `shared/types/dataflows.ts` parses, because the schemas there are the wire
 * contract and a client that guesses at it fails as a 422 the user cannot act
 * on.
 *
 * Mutations are never auto-retried (`lib/queryClient.ts` sets that default):
 * a retried validate re-sends a credential to a vendor, and a retried delete
 * races its own success.
 */

export const DATAFLOWS_PATH = "/api/v2/dataflows";

export function dataflowPath(id: string): string {
  return `${DATAFLOWS_PATH}/${encodeURIComponent(id)}`;
}

/** Every connection in the selected instance. Carries no field values at all. */
export function useDataflows(enabled = true): UseQueryResult<DataflowSummary[]> {
  const { selectedInstance } = useTenantInstance();
  return useQuery<DataflowSummary[]>({
    ...listQueryOptions,
    queryKey: instanceKey(DATAFLOWS_PATH, selectedInstance?.id),
    enabled,
  });
}

/**
 * A key that cannot collide with a real request path, for the disabled case.
 *
 * A disabled query still READS whatever is cached under its key, so falling
 * back to the list path would hand the editor the connection ARRAY typed as one
 * connection — and `detail.fields` would be undefined on a screen that had
 * merely not opened a saved connection yet. It must be a key nothing writes.
 */
const NO_DATAFLOW_KEY = "dataflow-detail:none";

/**
 * One connection, with its fields masked and its entities (FR-43).
 *
 * `staleTime: 0` on purpose: reopening the editor after a save must show what
 * the server now holds, not a copy from before the write. This is also an
 * audited read (SEC-11), so it is not fetched speculatively.
 */
export function useDataflow(id: string | null | undefined): UseQueryResult<DataflowDetail> {
  return useQuery<DataflowDetail>({
    queryKey: [id ? dataflowPath(id) : NO_DATAFLOW_KEY],
    enabled: Boolean(id),
    staleTime: 0,
  });
}

/** The cache entries a write to one connection invalidates: the list, the detail, its pages. */
export function useInvalidateDataflows() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    void queryClient.invalidateQueries({ queryKey: [DATAFLOWS_PATH] });
    // A Validate or a save moves the credential check line and the expiry dates too.
    void queryClient.invalidateQueries({ queryKey: [CREDENTIALS_PATH] });
    if (id) void queryClient.invalidateQueries({ queryKey: [dataflowPath(id)] });
    // #2491: Your data names the example table from the connections' selected tables.
    queryClient.invalidateQueries({ queryKey: [DATA_LOCATION_PATH] }).catch(() => undefined);
  };
}

/**
 * Round 6, S2: the client never names a refresh type on a create; the server stores the
 * connector's capability and the trigger (a schedule row, Run now) decides full or incremental.
 */
type CreateDataflowRequest = Omit<CreateDataflowBody, "refreshType">;

export function useCreateDataflow(): UseMutationResult<
  DataflowDetail,
  Error,
  CreateDataflowRequest
> {
  const invalidate = useInvalidateDataflows();
  return useMutation({
    mutationFn: (body: CreateDataflowRequest) =>
      apiRequest<DataflowDetail>(DATAFLOWS_PATH, { method: "POST", body }),
    onSuccess: (detail) => invalidate(detail.id),
  });
}

export function useUpdateDataflow(
  id: string,
): UseMutationResult<DataflowDetail, Error, UpdateDataflowBody> {
  const invalidate = useInvalidateDataflows();
  return useMutation({
    mutationFn: (body: UpdateDataflowBody) =>
      apiRequest<DataflowDetail>(dataflowPath(id), { method: "PUT", body }),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteDataflow(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidateDataflows();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(dataflowPath(id), { method: "DELETE" }),
    onSuccess: () => invalidate(),
  });
}

/**
 * 2026-09-28: turn a connection off (a reason is required) or on again (optional). Each answers
 * the connection's detail and invalidates the list and that detail, so the list reloads.
 */
export function useDisableDataflow(): UseMutationResult<
  DataflowDetail,
  Error,
  { id: string; reason: string }
> {
  const invalidate = useInvalidateDataflows();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      apiRequest<DataflowDetail>(`${dataflowPath(id)}/disable`, {
        method: "POST",
        body: { reason },
      }),
    onSuccess: (_detail, { id }) => invalidate(id),
  });
}

export function useEnableDataflow(): UseMutationResult<
  DataflowDetail,
  Error,
  { id: string; reason?: string }
> {
  const invalidate = useInvalidateDataflows();
  return useMutation({
    mutationFn: ({ id, reason }) =>
      apiRequest<DataflowDetail>(`${dataflowPath(id)}/enable`, {
        method: "POST",
        body: reason ? { reason } : {},
      }),
    onSuccess: (_detail, { id }) => invalidate(id),
  });
}

/**
 * FR-41 on a form that has never been saved. A vendor rejection is a 200 whose
 * `status` is `failed` — the request worked and the answer is "those
 * credentials are wrong" — so the caller reads `status`, not the promise.
 */
export function useValidateDataflow(): UseMutationResult<
  ValidationView & { referenceId?: string },
  Error,
  ValidateDataflowBody
> {
  return useMutation({
    // #2485: the answer's reference id, for the "could not reach" card's Copy details.
    mutationFn: async (body: ValidateDataflowBody) => {
      const answer = await apiRequestEnvelope<ValidationView>(`${DATAFLOWS_PATH}/validate`, {
        method: "POST",
        body,
      });
      return { ...(answer.data as ValidationView), referenceId: answer.correlationId };
    },
  });
}

export interface RevalidateResult {
  validation: ValidationView;
  dataflow: DataflowDetail;
  /**
   * #1098: false for a test of values the connection does not hold; the server then changed
   * nothing on it (state, "Last validated", tables). Absent from an older server.
   */
  recorded?: boolean;
  /** SD2 (#1465): the test learned nothing and changed nothing. */
  noVerdict?: boolean;
  /** #2485: the answer's reference id (client side), for the "could not reach" card. */
  referenceId?: string;
}

/**
 * FR-43. Only the fields the user actually retyped travel; everything else is
 * resolved from the stored `secret_ref` on the server, so an unchanged
 * credential is never re-sent and never re-typed.
 */
export function useRevalidateDataflow(
  id: string,
): UseMutationResult<RevalidateResult, Error, { fields: CreateDataflowBody["fields"] }> {
  const invalidate = useInvalidateDataflows();
  return useMutation({
    mutationFn: async (body: { fields: CreateDataflowBody["fields"] }) => {
      const answer = await apiRequestEnvelope<RevalidateResult>(`${dataflowPath(id)}/revalidate`, {
        method: "POST",
        body,
      });
      return { ...(answer.data as RevalidateResult), referenceId: answer.correlationId };
    },
    onSuccess: () => invalidate(id),
  });
}

export interface EntityPage {
  items: DataflowEntityView[];
  page: number;
  pageSize: number;
  /** Rows matching the search and view across every page — what "select all matching" selects. */
  total: number;
}

export function entitiesPath(id: string, query: EntitiesQuery): string {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
    selected: query.selected,
  });
  if (query.search) params.set("search", query.search);
  if (query.entityType !== "all") params.set("entityType", query.entityType);
  for (const name of query.names ?? []) params.append("names", name);
  return `${dataflowPath(id)}/entities?${params.toString()}`;
}

/**
 * One page of a saved connection's catalogue, searched and filtered on the server
 * (legacy issue A1/A2): the client never holds a 7,000-table list. The key starts with
 * the connection's own path so every write's invalidation reaches every cached page,
 * and `keepPreviousData` keeps the grid painted while the next page or search lands.
 * `initial` is the first page the detail already carried, stamped with the detail's own
 * fetch time so it is exactly as fresh as the detail is: opening the editor costs no
 * second request, and a page older than `PAGE_STALE_MS` is refetched on the next mount.
 */
export function useDataflowEntities(
  id: string | null | undefined,
  query: EntitiesQuery,
  initial?: { page: EntityPage; fetchedAt: number },
): UseQueryResult<EntityPage> {
  return useQuery<EntityPage>({
    ...listQueryOptions,
    queryKey: [id ? dataflowPath(id) : NO_DATAFLOW_KEY, "entities", query],
    enabled: Boolean(id),
    queryFn: async ({ signal }) => {
      const envelope = await apiRequestEnvelope<DataflowEntityView[]>(
        entitiesPath(id as string, query),
        { signal },
      );
      return {
        items: envelope.data ?? [],
        page: envelope.meta?.page ?? query.page,
        pageSize: envelope.meta?.pageSize ?? query.pageSize,
        total: envelope.meta?.total ?? 0,
      };
    },
    placeholderData: keepPreviousData,
    initialData: initial?.page,
    initialDataUpdatedAt: initial?.fetchedAt,
    staleTime: PAGE_STALE_MS,
  });
}

/**
 * Short, so a page is not refetched on every key change back to it, and so every write's
 * invalidation (which refetches regardless of staleness) stays the thing that moves it.
 */
const PAGE_STALE_MS = 5_000;

/**
 * FR-44 as deltas: the ticks and per-entity settings the user changed on the pages they
 * visited, plus "select everything matching this search", which the server resolves
 * against the same predicate the listing used. The body carries `expectedUpdatedAt`, so a
 * selection saved over someone else's is a 409 `STALE_UPDATE` rather than a lost update.
 */
export function usePatchEntities(
  id: string,
): UseMutationResult<DataflowDetail, Error, PatchEntitiesBody> {
  const invalidate = useInvalidateDataflows();
  return useMutation({
    mutationFn: (body: PatchEntitiesBody) =>
      apiRequest<DataflowDetail>(`${dataflowPath(id)}/entities`, { method: "PATCH", body }),
    onSuccess: () => invalidate(id),
  });
}

/**
 * FR-45. A connector that cannot browse answers 422 `CONNECTOR_UNSUPPORTED`,
 * which arrives as an `ApiClientError` and is shown inline in the dialog rather
 * than as a toast: it describes the screen the user is on, not an event.
 */
export function useBrowseDataflow(
  id: string,
): UseMutationResult<BrowseView, Error, BrowseDataflowBody> {
  return useMutation({
    mutationFn: (body: BrowseDataflowBody) =>
      apiRequest<BrowseView>(`${dataflowPath(id)}/browse`, { method: "POST", body }),
  });
}
